import type { Skill } from '../types';

const SKILL_LEVEL_ORDER: Record<string, number> = {
  foundation: 0,
  developing: 1,
  intermediate: 2,
  proficient: 3,
  advanced: 4,
  expert: 5,
  mastery: 6
};

export function getSkillLevelOrder(level: string): number {
  return SKILL_LEVEL_ORDER[level] ?? -1;
}

export function isLowerLevel(level1: string, level2: string): boolean {
  return getSkillLevelOrder(level1) < getSkillLevelOrder(level2);
}

export function isAdjacentLevel(dependencyLevel: string, currentLevel: string): boolean {
  const depOrder = getSkillLevelOrder(dependencyLevel);
  const currOrder = getSkillLevelOrder(currentLevel);
  return currOrder - depOrder === 1;
}

export function detectCircularDependency(
  skillId: string,
  newDependencies: string[],
  allSkills: Skill[]
): boolean {
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  const hasCycle = (currentId: string): boolean => {
    visited.add(currentId);
    recursionStack.add(currentId);

    const skill = allSkills.find(s => s.id === currentId);
    if (!skill) {
      recursionStack.delete(currentId);
      return false;
    }

    const dependencies = currentId === skillId ? newDependencies : (skill.dependsOn || []);

    for (const depId of dependencies) {
      if (!visited.has(depId)) {
        if (hasCycle(depId)) {
          return true;
        }
      } else if (recursionStack.has(depId)) {
        return true;
      }
    }

    recursionStack.delete(currentId);
    return false;
  };

  return hasCycle(skillId);
}

export function validateDependencies(
  skillId: string,
  newDependencies: string[],
  allSkills: Skill[]
): { valid: boolean; error?: string } {
  if (newDependencies.includes(skillId)) {
    return {
      valid: false,
      error: 'A skill cannot depend on itself'
    };
  }

  if (newDependencies.length !== new Set(newDependencies).size) {
    return {
      valid: false,
      error: 'Cannot add duplicate dependencies'
    };
  }

  const currentSkill = allSkills.find(s => s.id === skillId);
  if (!currentSkill) {
    return {
      valid: false,
      error: 'Current skill not found'
    };
  }

  const currentLevelOrder = getSkillLevelOrder(currentSkill.level);

  for (const depId of newDependencies) {
    const depSkill = allSkills.find(s => s.id === depId);
    if (!depSkill) {
      return {
        valid: false,
        error: 'One or more dependencies reference non-existent skills'
      };
    }

    if (!isAdjacentLevel(depSkill.level, currentSkill.level)) {
      const depLevelOrder = getSkillLevelOrder(depSkill.level);
      if (depLevelOrder >= currentLevelOrder) {
        return {
          valid: false,
          error: `Prerequisites must be from the immediately preceding level. "${depSkill.name}" (${depSkill.level}) cannot be a prerequisite for "${currentSkill.name}" (${currentSkill.level}). Skills can only depend on skills from the directly preceding level.`
        };
      } else {
        return {
          valid: false,
          error: `Prerequisites must be from adjacent levels only. "${depSkill.name}" (${depSkill.level}) is not adjacent to "${currentSkill.name}" (${currentSkill.level}). Skills can only depend on skills from the immediately preceding level (Foundation → Developing → Intermediate → Proficient → Advanced → Expert → Mastery).`
        };
      }
    }
  }

  if (detectCircularDependency(skillId, newDependencies, allSkills)) {
    return {
      valid: false,
      error: 'This dependency would create a circular reference. A skill cannot depend on another skill that directly or indirectly depends on it.'
    };
  }

  return { valid: true };
}
